import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  RefreshCw, 
  AlertTriangle, 
  X, 
  CheckCircle,
  UserCheck
} from 'lucide-react';
import MainService from '../../services/MainService';

const AddAgent = () => {
  const navigate = useNavigate();
  const { agentId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Agent form data
  const [agent, setAgent] = useState({
    Id: 0,
    Name: '',
    MobileNo1: '',
    MobileNo2: '',
    EmailId: '',
    Address: '',
    Remarks: '',
    CommissionPer: 0,
    IsActive: true
  });

  useEffect(() => {
    if (agentId && parseInt(agentId) > 0) {
      loadAgent(parseInt(agentId));
    }
  }, [agentId]);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadAgent = async (id) => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.AgentById(id);
      if (response) {
        setAgent({
          Id: response.Id || 0,
          Name: response.Name || '',
          MobileNo1: response.MobileNo1 || '',
          MobileNo2: response.MobileNo2 || '',
          EmailId: response.EmailId || '',
          Address: response.Address || '',
          Remarks: response.Remarks || '',
          CommissionPer: response.CommissionPer || 0,
          IsActive: response.IsActive !== undefined ? response.IsActive : true
        });
      }
    } catch (err) {
      console.error('Error loading agent:', err);
      setError('Failed to load agent data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setAgent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!agent.Name || agent.Name.trim() === '') {
      setError('Enter Name');
      return;
    }
    if (!agent.MobileNo1 || agent.MobileNo1.trim() === '') {
      setError('Enter Mobile Number');
      return;
    }
    if (!agent.CommissionPer || parseFloat(agent.CommissionPer) === 0) {
      setError('Enter Commission');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await MainService.SaveAgent(agent);
      
      if (response === 'Success' || response === true) {
        setSuccess('Agent saved successfully');
        setTimeout(() => {
          navigate('/home/accounts/agents');
        }, 1500);
      } else {
        setError(response || 'Failed to save agent');
      }
    } catch (err) {
      console.error('Error saving agent:', err);
      setError('Failed to save agent');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAgent({
      Id: 0,
      Name: '',
      MobileNo1: '',
      MobileNo2: '',
      EmailId: '',
      Address: '',
      Remarks: '',
      CommissionPer: 0,
      IsActive: true
    });
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-1 border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <UserCheck className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">
                {agentId && parseInt(agentId) > 0 ? 'Edit Agent' : 'Add Agent'}
              </h1>
            </div>
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              {loading && (
                <div className="w-6 h-6 border-2 border-[#46955A] border-t-transparent rounded-full animate-spin"></div>
              )}
              <button
                onClick={handleReset}
                disabled={loading}
                className="flex items-center gap-2 px-4 py-2 border border-[#46955A] text-[#46955A] rounded-lg hover:bg-[#f0f9f4] hover:border-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Reset</span>
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Save className="w-4 h-4" />
                <span>Save</span>
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 ml-8">
            {agentId && parseInt(agentId) > 0 
              ? 'Update agent information and details' 
              : 'Create new agent and manage their information'}
          </p>
        </div>
      </div>

      {/* Form Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={agent.Name}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Mobile No1 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={agent.MobileNo1}
                onChange={(e) => handleInputChange('MobileNo1', e.target.value)}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Mobile No2</label>
              <input
                type="text"
                value={agent.MobileNo2}
                onChange={(e) => handleInputChange('MobileNo2', e.target.value)}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={agent.EmailId}
                onChange={(e) => handleInputChange('EmailId', e.target.value)}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Commission % <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={agent.CommissionPer}
                onChange={(e) => handleInputChange('CommissionPer', parseFloat(e.target.value) || 0)}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
                required
              />
            </div>
            <div className="space-y-2 md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700">Address</label>
              <textarea
                rows="4"
                value={agent.Address}
                onChange={(e) => handleInputChange('Address', e.target.value)}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent resize-none"
              />
            </div>
            <div className="space-y-2 md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700">Remarks</label>
              <textarea
                rows="4"
                value={agent.Remarks}
                onChange={(e) => handleInputChange('Remarks', e.target.value)}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent resize-none"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAgent;
